import { useState } from 'react'
import type { Database } from '@/lib/supabase'
import ScrollArea from '../ui/shared/ScrollArea'
import { useTranslation } from "react-i18next";

type AccountTemplate = Database['public']['Tables']['account_templates']['Row']

interface TemplatesMenuProps {
  templates: AccountTemplate[]
  onSelectTemplate: (template: AccountTemplate) => void
  loading?: boolean
}

export const TemplatesMenu = ({ templates, onSelectTemplate, loading }: TemplatesMenuProps) => {
  const { t } = useTranslation("chat");
  const [searchTerm, setSearchTerm] = useState('')

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.shortcut && template.shortcut.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    const category = template.category || 'Geral'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(template)
    return acc
  }, {} as Record<string, AccountTemplate[]>)

  if (loading) {
    return (
      <div className="w-[320px] bg-card rounded-lg shadow-md border border-border p-4 dark:bg-neutral-800 dark:border-neutral-700 dark:shadow-xl">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-card-muted dark:bg-neutral-700 rounded w-3/4"></div>
          <div className="h-4 bg-card-muted dark:bg-neutral-700 rounded w-1/2"></div>
          <div className="h-4 bg-card-muted dark:bg-neutral-700 rounded w-2/3"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-[360px] bg-card rounded-lg shadow-md border border-border overflow-hidden dark:bg-neutral-800 dark:border-neutral-700 dark:shadow-xl">
      <div className="p-3 border-b border-border dark:border-neutral-700">
        <h3 className="text-sm font-semibold text-foreground dark:text-neutral-50 mb-2">{t("pickers.quickReplies")}</h3>
        <input
          type="text"
          placeholder={t("common.searchTemplate")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200 dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50 dark:placeholder-neutral-400 dark:focus:ring-blue-500"
        />
      </div>

      <ScrollArea classNames={{ root: "max-h-[400px]" }}>
        <div className="p-2">
          {Object.keys(groupedTemplates).length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-muted-foreground/40 dark:text-neutral-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm text-muted-foreground dark:text-neutral-400">
                {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template cadastrado'}
              </p>
              {!searchTerm && (
                <p className="text-xs text-muted-foreground dark:text-neutral-500 mt-1">
                  {t("pickers.templatesHint")}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(groupedTemplates).map(([category, categoryTemplates]: [string, AccountTemplate[]]) => (
                <div key={category}>
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground dark:text-neutral-400 uppercase tracking-wide">
                    {category}
                  </div>
                  <div className="space-y-1">
                    {categoryTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => onSelectTemplate(template)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F8FAFC] dark:hover:bg-neutral-700 transition-colors duration-200 group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-foreground dark:text-neutral-50 truncate">
                                {template.name}
                              </span>
                              {template.shortcut && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-[#EEF4FF] text-primary border border-border dark:bg-neutral-900 dark:text-blue-400 dark:border-neutral-600">
                                  {template.shortcut}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground dark:text-neutral-400 line-clamp-2">
                              {template.content}
                            </p>
                          </div>
                          <svg
                            className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground dark:text-neutral-600 dark:group-hover:text-neutral-400 transition-colors duration-200 flex-shrink-0 mt-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="p-2 border-t border-border bg-[#F8FAFC] dark:border-neutral-700 dark:bg-neutral-900">
        <p className="text-xs text-muted-foreground dark:text-neutral-500 text-center">
          {t("pickers.use")} <span className="font-mono text-primary dark:text-blue-400">/atalho</span> {t("pickers.toSearchFast")}
        </p>
      </div>
    </div>
  )
}
